import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ShieldAlert, Lock, CheckCircle, ChevronDown, ChevronLeft, ChevronRight,
  AlertTriangle, Loader, FileSignature, Languages, RotateCcw, ScrollText, ShieldCheck,
} from 'lucide-react';
import { SignaturePad } from './LeaseSignCard';

// Security Declaration — the driver's side of Australia Post's Parcel Contractor
// Security Briefing.
//
// This is not a safety brief with a tick at the bottom. It is a signed instrument
// whose own words say that four of its six clauses describe criminal offences, and
// the whole shape of this screen exists to make an acknowledgement of that TRUE:
//
//   • ONE CLAUSE PER SCREEN. Six clauses on one page is six clauses nobody reads.
//     Each gets its own screen, has to be scrolled to the end, and is accepted on
//     its own — six timestamps instead of one signature at the bottom.
//   • PLAIN ENGLISH FIRST, THEN THE INSTRUMENT. The office's plain-language version
//     is what makes it understood; Australia Post's exact wording is what is signed.
//     Both are shown, always, and the boxed one is labelled as the words being signed.
//   • A TRANSLATION SITS BESIDE THE ENGLISH, NEVER INSTEAD OF IT. A declaration that
//     exists only as a machine translation is a weaker instrument — and a driver
//     signing words they cannot read is worse than both. So: both, together.
//   • THE CHECK IS NOT A TEST TO PASS. A wrong answer is recorded, then the clause is
//     re-opened and read again. Nobody fails; the evidence just shows what needed
//     explaining, and it prints on the certificate.
//
// Marking, timestamps and the answer key all live on the server. This screen cannot
// let anybody skip a step, and it never learns which option is correct.

const API = '/.netlify/functions/security-declaration-sb';
const LANG_KEY = 'hp_safety_lang';           // shared with the safety portal, on purpose

export const getDeclLang = () => {
  try { return localStorage.getItem(LANG_KEY) || 'en'; } catch { return 'en'; }
};
const setDeclLang = (c) => { try { localStorage.setItem(LANG_KEY, c); } catch { /* private mode */ } };

async function post(payload) {
  const res = await fetch(API, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

// The authoritative check, called at the moment the driver reaches their onboard scan.
// A network failure returns "not blocked": nobody gets trapped at the depot because a
// request timed out. The gate is a control, not a trap.
export async function checkSecurityGate(lang) {
  try { return await post({ action: 'gate', lang: lang || getDeclLang() }); }
  catch { return { success: false, blocked: false }; }
}

const LANG_FALLBACK = [
  { code: 'en', label: 'English', flag: '🇦🇺', native: 'English' },
  { code: 'es', label: 'Spanish', flag: '🇪🇸', native: 'Español' },
  { code: 'zh', label: 'Mandarin', flag: '🇨🇳', native: '中文' },
  { code: 'mn', label: 'Mongolian', flag: '🇲🇳', native: 'Монгол' },
];

// The screen's own furniture. Kept small and deliberately separate from the
// declaration content, which is translated by Claude on the server.
const UI = {
  en: {
    kicker: 'Security declaration', begin: 'Start the briefing', resume: 'Continue where you left off',
    clause: 'Clause', of: 'of', scroll: 'Scroll to the end to continue',
    apWords: 'Australia Post’s words — this is what you are signing',
    aid: 'Translation — provided to help you understand. The English above is what is signed.',
    offence: 'Criminal offence', understood: 'I understand clause', back: 'Back', next: 'Next',
    checkTitle: 'Understanding check', question: 'Question', notQuite: 'Not quite',
    reread: 'Read the clause again', tryAgain: 'Try again', correct: 'That’s right',
    signTitle: 'Sign the declaration', signHint: 'Sign with your finger',
    signBtn: 'Sign and submit', signing: 'Signing…',
    doneTitle: 'Signed', doneBody: 'A copy is in My Docs. You can open it any time.',
    already: 'You signed this declaration', continueBtn: 'Continue',
    minutes: 'about 5 minutes · once a year',
    yourName: 'Your name', contract: 'Contract number', identity: 'Signing as',
    translateBtn: 'Translate this for me', translating: 'Translating…',
    reviewTitle: 'What you have acknowledged', ackBox: 'Tick to confirm before you sign',
    onlyWhile: 'This only holds while',
  },
};
const t = (lang, key) => (UI[lang] && UI[lang][key]) || UI.en[key];

const wrapStyle = {
  background: 'var(--hp-surface)', border: '2px solid #b91c1c',
  borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 16,
};
const btnPrimary = (enabled, colour = '#b91c1c') => ({
  width: '100%', padding: '14px', border: 'none', borderRadius: 'var(--radius)',
  background: enabled ? colour : 'var(--hp-card)', color: enabled ? '#fff' : 'var(--hp-dim)',
  fontWeight: 800, fontSize: '0.92rem', cursor: enabled ? 'pointer' : 'not-allowed',
  fontFamily: 'var(--font-body)', display: 'inline-flex', alignItems: 'center',
  justifyContent: 'center', gap: 7,
});

function LangPicker({ langs, lang, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
      {(langs || LANG_FALLBACK).map(l => (
        <button key={l.code} type="button" onClick={() => onChange(l.code)}
          style={{
            padding: '4px 9px', borderRadius: 6, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
            border: `1px solid ${lang === l.code ? '#b91c1c' : 'var(--hp-border)'}`,
            background: lang === l.code ? 'rgba(185,28,28,0.12)' : 'transparent',
            color: lang === l.code ? '#ef4444' : 'var(--hp-dim)', fontFamily: 'var(--font-body)',
          }}>{l.flag} {l.native}</button>
      ))}
    </div>
  );
}

// A scroll box that will not report "read" until it has actually been scrolled to the
// bottom — and unlocks immediately when the content is shorter than the box, so a
// short clause never leaves someone hunting for a scrollbar that isn't there.
function ReadBox({ children, onRead, resetKey, maxHeight = 330 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = 0;
    if (el.scrollHeight <= el.clientHeight + 8) onRead();
  }, [resetKey, onRead]);
  return (
    <div ref={ref}
      onScroll={(e) => {
        const el = e.currentTarget;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) onRead();
      }}
      style={{ maxHeight, overflowY: 'auto', padding: '0 18px', WebkitOverflowScrolling: 'touch' }}>
      {children}
    </div>
  );
}

// The standing authorisation. Deliberately GREEN in a red screen: everything else here
// is a prohibition, and this is the one block that tells a driver what they ARE allowed
// to do. It renders on the cover and again above clauses 1 and 2 — the two the permission
// qualifies — because a driver who meets clause 1 without it will read "not any place,
// including my home" and conclude the depot has been telling them to commit an offence.
function AuthorityPanel({ authority, lang, compact }) {
  if (!authority || !authority.allowed) return null;
  return (
    <div style={{
      border: '1px solid rgba(22,163,74,0.55)', background: 'rgba(22,163,74,0.08)',
      borderRadius: 8, padding: compact ? '11px 13px' : '13px 15px', marginTop: 12,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap',
        fontSize: '0.6rem', fontWeight: 900, letterSpacing: '0.09em', textTransform: 'uppercase',
        color: '#16a34a', marginBottom: 8,
      }}>
        <ShieldCheck size={12} style={{ flexShrink: 0 }} />
        <span style={{ minWidth: 0 }}>{authority.title}</span>
      </div>
      {authority.intro && (
        <div style={{ fontSize: '0.79rem', color: 'var(--hp-text)', opacity: 0.85, lineHeight: 1.55, marginBottom: 9 }}>
          {authority.intro}
        </div>
      )}
      <div style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--hp-text)', lineHeight: 1.5 }}>
        {authority.allowed}
      </div>
      {!!(authority.conditions || []).length && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 6 }}>
            {t(lang, 'onlyWhile')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {authority.conditions.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                <CheckCircle size={13} color="#16a34a" style={{ flexShrink: 0, marginTop: 3 }} />
                <div style={{ flex: 1, minWidth: 0, fontSize: '0.81rem', color: 'var(--hp-text)', lineHeight: 1.5 }}>{c}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {authority.warning && (
        <div style={{
          marginTop: 10, paddingTop: 9, borderTop: '1px solid rgba(22,163,74,0.3)',
          fontSize: '0.78rem', color: 'var(--hp-text)', opacity: 0.9, lineHeight: 1.55,
        }}>
          {authority.warning}
        </div>
      )}
    </div>
  );
}

// One clause, rendered the same way everywhere it appears — on its own screen, and
// again inline when a wrong answer sends the driver back to it.
function ClauseBody({ clause, lang, compact, authority }) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{
          fontSize: '0.62rem', fontWeight: 900, letterSpacing: '0.1em', color: 'var(--hp-dim)',
          textTransform: 'uppercase',
        }}>{t(lang, 'clause')} {clause.n}</span>
        {clause.offence && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(185,28,28,0.14)',
            color: '#ef4444', border: '1px solid rgba(185,28,28,0.5)', borderRadius: 5,
            padding: '2px 7px', fontSize: '0.6rem', fontWeight: 900, letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}><AlertTriangle size={10} /> {t(lang, 'offence')}</span>
        )}
      </div>

      <div style={{ fontSize: compact ? '1rem' : '1.15rem', fontWeight: 800, color: 'var(--hp-text)', lineHeight: 1.3 }}>
        {clause.heading}
      </div>
      {clause.authority && <AuthorityPanel authority={authority} lang={lang} compact />}
      <div style={{ fontSize: '0.87rem', color: 'var(--hp-text)', opacity: 0.85, marginTop: 8, lineHeight: 1.55, whiteSpace: 'pre-line' }}>
        {clause.plain}
      </div>

      {/* The instrument itself. Visually set apart, and labelled, so nobody can
          later say they thought the plain-English summary was the thing they signed. */}
      <div style={{
        marginTop: 14, padding: '12px 14px', background: 'var(--hp-black)',
        border: '1px solid var(--hp-border)', borderRadius: 8,
      }}>
        <div style={{
          fontSize: '0.58rem', fontWeight: 900, letterSpacing: '0.09em', textTransform: 'uppercase',
          color: 'var(--hp-gold)', marginBottom: 7, lineHeight: 1.4,
        }}>{t(lang, 'apWords')}</div>
        <div style={{ fontSize: '0.82rem', color: 'var(--hp-text)', lineHeight: 1.6 }}>{clause.text}</div>

        {clause.textTranslated && (
          <>
            <div style={{ height: 1, background: 'var(--hp-border)', margin: '12px 0' }} />
            <div style={{ fontSize: '0.82rem', color: 'var(--hp-text)', opacity: 0.9, lineHeight: 1.6 }}>
              {clause.textTranslated}
            </div>
            <div style={{ fontSize: '0.63rem', color: 'var(--hp-dim)', marginTop: 8, lineHeight: 1.45, fontStyle: 'italic' }}>
              {t(lang, 'aid')}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// The gate
// ══════════════════════════════════════════════════════════════════════════════
// `preview` makes the whole thing inert: the office reads it exactly as a driver
// would, and not one byte is written — no begin, no clause acks, no answers, no
// signature. The preview is fed a projected declaration and marks its own answers
// from a key it is given, which is the only place in this file that happens.
export function SecurityDeclarationGate({ payload, lang: langProp, onCleared, onLangChange, preview, previewKey }) {
  const [lang, setLang] = useState(langProp || getDeclLang());
  const [data, setData] = useState(payload || null);
  const [loading, setLoading] = useState(!payload);
  const [step, setStep] = useState('cover');       // cover | clause | quiz | sign | done
  const [idx, setIdx] = useState(0);
  const [acked, setAcked] = useState({});          // clauseId → true
  const [read, setRead] = useState(false);
  const [answers, setAnswers] = useState({});      // questionId → { correct, explain, clause, picked }
  const [picked, setPicked] = useState('');
  const [signature, setSignature] = useState('');
  const [finalTick, setFinalTick] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [translating, setTranslating] = useState(false);

  useEffect(() => { if (langProp) setLang(langProp); }, [langProp]);

  const load = useCallback(async (code) => {
    if (preview) return;
    setLoading(true);
    const d = await post({ action: 'view', lang: code });
    if (d && d.success) {
      setData(d);
      // A sitting picked up later resumes exactly where it stopped — the office
      // launches this on a morning, and a driver interrupted at the depot should
      // not have to read all six clauses again.
      if (d.progress) {
        setAcked(Object.fromEntries(Object.keys(d.progress.clauses || {}).map(k => [k, true])));
        setAnswers(Object.fromEntries(Object.entries(d.progress.quiz || {})
          .filter(([, v]) => v.correctAt).map(([k]) => [k, { correct: true }])));
      }
    } else setError((d && d.error) || 'Could not load the declaration.');
    setLoading(false);
  }, [preview]);

  useEffect(() => { if (!payload) load(lang); }, [load, lang, payload]);
  useEffect(() => { if (payload) setData(payload); }, [payload]);

  const decl = data && data.declaration;
  const clauses = (decl && decl.clauses) || [];
  const quiz = (decl && decl.quiz) || [];
  const who = (data && data.identity) || {};

  const allAcked = clauses.length > 0 && clauses.every(c => acked[c.id]);
  const allAnswered = quiz.length > 0 && quiz.every(q => answers[q.id] && answers[q.id].correct);

  const changeLang = async (code) => {
    setLang(code); setRead(false);
    if (!preview) { setDeclLang(code); await load(code); }
    onLangChange && onLangChange(code);
  };

  const translateForMe = async () => {
    if (preview) return;
    setTranslating(true); setError('');
    const d = await post({ action: 'requestTranslation', lang });
    if (d && d.success) await load(lang);
    else setError((d && d.error) || 'Could not translate just now.');
    setTranslating(false);
  };

  const begin = async () => {
    setError('');
    if (!preview) {
      const d = await post({ action: 'begin', lang });
      if (!d || !d.success) { setError((d && d.error) || 'Could not start.'); return; }
    }
    // Straight to the first clause they have not yet accepted.
    const first = clauses.findIndex(c => !acked[c.id]);
    setIdx(first < 0 ? 0 : first);
    setRead(false);
    setStep(first < 0 && allAnswered ? 'sign' : first < 0 ? 'quiz' : 'clause');
  };

  const acceptClause = async () => {
    const c = clauses[idx];
    if (!c) return;
    setBusy(true); setError('');
    if (!preview) {
      const d = await post({ action: 'ackClause', clauseId: c.id, lang });
      if (!d || !d.success) { setError((d && d.error) || 'Could not record that.'); setBusy(false); return; }
    }
    const next = { ...acked, [c.id]: true };
    setAcked(next); setBusy(false); setRead(false);
    const remaining = clauses.findIndex(x => !next[x.id]);
    if (remaining >= 0) { setIdx(remaining); }
    else { setIdx(0); setStep('quiz'); }
  };

  const submitAnswer = async () => {
    const q = quiz[idx];
    if (!q || !picked) return;
    setBusy(true); setError('');
    let result;
    if (preview) {
      // The only place a client marks anything, and only ever against a key handed
      // to the preview by the office's own admin call.
      const key = ((data.answerKey || {})[q.id] || '');
      result = { success: true, correct: picked === key, explain: q.wrong, clause: q.clause };
    } else {
      result = await post({ action: 'answer', questionId: q.id, choice: picked, lang });
    }
    setBusy(false);
    if (!result || !result.success) { setError((result && result.error) || 'Could not record that.'); return; }
    setAnswers(a => ({ ...a, [q.id]: { correct: result.correct, explain: result.explain, clause: result.clause, picked } }));
  };

  const nextQuestion = () => {
    setPicked('');
    const remaining = quiz.findIndex(q => !(answers[q.id] && answers[q.id].correct));
    if (remaining >= 0) setIdx(remaining);
    else setStep('sign');
  };

  const retry = () => {
    const q = quiz[idx];
    setAnswers(a => { const n = { ...a }; delete n[q.id]; return n; });
    setPicked('');
  };

  const submitSignature = async () => {
    if (!signature || !finalTick) return;
    setBusy(true); setError('');
    if (preview) {
      setDone({ verifyCode: 'HP-PREVIEW', preview: true });
      setStep('done'); setBusy(false); return;
    }
    const d = await post({ action: 'sign', signature, lang });
    setBusy(false);
    if (!d || !d.success) { setError((d && d.error) || 'Could not sign.'); return; }
    setDone(d.record || {});
    setStep('done');
  };

  // ── frames ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ ...wrapStyle, padding: 30, textAlign: 'center', color: 'var(--hp-dim)' }}>
        <Loader size={18} style={{ animation: 'spin 0.9s linear infinite' }} />
        <div style={{ marginTop: 8, fontSize: '0.84rem' }}>Loading your security briefing…</div>
      </div>
    );
  }
  if (!decl) {
    return <div style={{ ...wrapStyle, padding: 24, color: 'var(--hp-dim)', fontSize: '0.85rem' }}>{error || 'Not available.'}</div>;
  }

  const header = (
    <div style={{ background: '#b91c1c', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <ShieldAlert size={15} color="#fff" />
      <span style={{
        fontSize: '0.66rem', fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: '#fff', lineHeight: 1.3, flex: 1,
      }}>{t(lang, 'kicker')}</span>
      {step === 'clause' && (
        <span style={{ fontSize: '0.66rem', fontWeight: 800, color: 'rgba(255,255,255,0.85)' }}>
          {idx + 1} / {clauses.length}
        </span>
      )}
      {step === 'quiz' && (
        <span style={{ fontSize: '0.66rem', fontWeight: 800, color: 'rgba(255,255,255,0.85)' }}>
          {quiz.filter(q => answers[q.id] && answers[q.id].correct).length} / {quiz.length}
        </span>
      )}
    </div>
  );

  const errorLine = error ? (
    <div style={{ margin: '0 18px 12px', padding: '9px 11px', borderRadius: 8, background: 'rgba(185,28,28,0.12)', color: '#ef4444', fontSize: '0.78rem', fontWeight: 600 }}>{error}</div>
  ) : null;

  const previewNote = preview ? (
    <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 10, textAlign: 'center', lineHeight: 1.45 }}>
      Preview — nothing is recorded. This is exactly what a driver meets before their onboard scan.
    </div>
  ) : null;

  // ── already signed ────────────────────────────────────────────────────────
  if (!preview && step === 'cover' && data.signed) {
    return (
      <div style={{ ...wrapStyle, borderColor: '#16a34a' }}>
        <div style={{ background: '#16a34a', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle size={15} color="#fff" />
          <span style={{ fontSize: '0.66rem', fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#fff' }}>
            {t(lang, 'already')}
          </span>
        </div>
        <div style={{ padding: '16px 18px' }}>
          <div style={{ fontSize: '0.95rem', fontWeight: 800, color: 'var(--hp-text)' }}>{decl.title}</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 6, lineHeight: 1.5 }}>
            Signed {new Date(data.signed.signedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            {' · '}{data.signed.verifyCode}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 8, lineHeight: 1.5 }}>{t(lang, 'doneBody')}</div>
          {onCleared && (
            <button onClick={onCleared} style={{ ...btnPrimary(true, '#16a34a'), marginTop: 14 }}>
              <ChevronRight size={16} /> {t(lang, 'continueBtn')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── cover ─────────────────────────────────────────────────────────────────
  if (step === 'cover') {
    const startedAlready = !!(data.progress && Object.keys(data.progress.clauses || {}).length);
    return (
      <div style={wrapStyle}>
        {header}
        <div style={{ padding: '16px 18px 0' }}>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--hp-text)', lineHeight: 1.25 }}>{decl.title}</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 5, lineHeight: 1.45 }}>{decl.subtitle}</div>
          <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginTop: 8, fontStyle: 'italic' }}>{decl.source}</div>
        </div>
        <div style={{ padding: '14px 18px 0' }}>
          <div style={{ fontSize: '0.86rem', color: 'var(--hp-text)', lineHeight: 1.6 }}>{decl.intro}</div>
          <AuthorityPanel authority={decl.authority} lang={lang} />
          <div style={{
            marginTop: 14, padding: '11px 13px', background: 'var(--hp-black)',
            border: '1px solid var(--hp-border)', borderRadius: 8,
          }}>
            <div style={{ fontSize: '0.58rem', fontWeight: 900, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 6 }}>
              {t(lang, 'identity')}
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--hp-text)' }}>{who.name || who.login}</div>
            <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 3 }}>
              {who.login}{who.contractNumber ? ` · ${t(lang, 'contract')} ${who.contractNumber}` : ''}
            </div>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 10 }}>{t(lang, 'minutes')}</div>
        </div>
        <div style={{ padding: '14px 18px 16px' }}>
          <div style={{ marginBottom: 10 }}>
            <LangPicker langs={decl.langs} lang={lang} onChange={changeLang} />
          </div>
          {!preview && !decl.translated && lang !== 'en' && (
            <button onClick={translateForMe} disabled={translating}
              style={{ ...btnPrimary(!translating, 'var(--hp-card)'), color: 'var(--hp-text)', marginBottom: 10, border: '1px solid var(--hp-border)' }}>
              <Languages size={15} /> {translating ? t(lang, 'translating') : t(lang, 'translateBtn')}
            </button>
          )}
          {errorLine}
          <button onClick={begin} style={btnPrimary(true)}>
            <ScrollText size={16} /> {startedAlready ? t(lang, 'resume') : t(lang, 'begin')}
          </button>
          {previewNote}
        </div>
      </div>
    );
  }

  // ── one clause ────────────────────────────────────────────────────────────
  if (step === 'clause') {
    const c = clauses[idx];
    return (
      <div style={wrapStyle}>
        {header}
        <div style={{ padding: '14px 0 0' }}>
          <ReadBox onRead={() => setRead(true)} resetKey={`${c.id}-${lang}`}>
            <ClauseBody clause={c} lang={lang} authority={decl.authority} />
            <div style={{ height: 10 }} />
          </ReadBox>
        </div>
        <div style={{ padding: '12px 18px 16px' }}>
          {errorLine}
          {!read && (
            <div style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: 700, marginBottom: 9, display: 'flex', alignItems: 'center', gap: 5 }}>
              <ChevronDown size={13} /> {t(lang, 'scroll')}
            </div>
          )}
          <button onClick={acceptClause} disabled={!read || busy} style={btnPrimary(read && !busy)}>
            <CheckCircle size={16} /> {busy ? '…' : `${t(lang, 'understood')} ${c.n}`}
          </button>
          {idx > 0 && (
            <button onClick={() => { setIdx(idx - 1); setRead(false); }}
              style={{ ...btnPrimary(true, 'transparent'), color: 'var(--hp-dim)', marginTop: 8 }}>
              <ChevronLeft size={15} /> {t(lang, 'back')}
            </button>
          )}
          {previewNote}
        </div>
      </div>
    );
  }

  // ── understanding check ───────────────────────────────────────────────────
  if (step === 'quiz') {
    const q = quiz[idx];
    const a = answers[q.id];
    const clause = clauses.find(c => c.id === (a && a.clause ? a.clause : q.clause));
    return (
      <div style={wrapStyle}>
        {header}
        <div style={{ padding: '14px 18px 0' }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>
            {t(lang, 'checkTitle')} · {t(lang, 'question')} {idx + 1} {t(lang, 'of')} {quiz.length}
          </div>
          <div style={{ fontSize: '0.88rem', color: 'var(--hp-text)', marginTop: 10, lineHeight: 1.6 }}>{q.scenario}</div>
          <div style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--hp-text)', marginTop: 12, lineHeight: 1.35 }}>{q.question}</div>
        </div>

        <div style={{ padding: '12px 18px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {q.options.map(o => {
            const chosen = (a ? a.picked : picked) === o.id;
            const settled = !!a;
            const good = settled && a.correct && chosen;
            const bad = settled && !a.correct && chosen;
            return (
              <button key={o.id} type="button" disabled={settled || busy}
                onClick={() => setPicked(o.id)}
                style={{
                  textAlign: 'left', padding: '12px 13px', borderRadius: 8, cursor: settled ? 'default' : 'pointer',
                  border: `1.5px solid ${good ? '#16a34a' : bad ? '#b91c1c' : chosen ? '#b91c1c' : 'var(--hp-border)'}`,
                  background: good ? 'rgba(22,163,74,0.12)' : bad ? 'rgba(185,28,28,0.12)' : chosen ? 'rgba(185,28,28,0.08)' : 'var(--hp-black)',
                  color: 'var(--hp-text)', fontSize: '0.85rem', lineHeight: 1.5, fontFamily: 'var(--font-body)',
                  fontWeight: chosen ? 700 : 500,
                }}>{o.text}</button>
            );
          })}
        </div>

        {/* A wrong answer is not a buzzer. It re-opens the clause it tested, in full,
            and the driver reads it again before trying — which is the entire point of
            asking. The attempt is already recorded on the server by this moment. */}
        {a && !a.correct && (
          <div style={{ margin: '14px 18px 0', padding: '13px 14px', borderRadius: 8, background: 'rgba(185,28,28,0.1)', border: '1px solid rgba(185,28,28,0.4)' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#ef4444', display: 'flex', alignItems: 'center', gap: 5 }}>
              <AlertTriangle size={12} /> {t(lang, 'notQuite')}
            </div>
            <div style={{ fontSize: '0.83rem', color: 'var(--hp-text)', marginTop: 8, lineHeight: 1.55 }}>{a.explain}</div>
            {clause && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(185,28,28,0.3)' }}>
                <div style={{ fontSize: '0.6rem', fontWeight: 900, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 8 }}>
                  {t(lang, 'reread')}
                </div>
                <ClauseBody clause={clause} lang={lang} compact authority={decl.authority} />
              </div>
            )}
          </div>
        )}

        <div style={{ padding: '14px 18px 16px' }}>
          {errorLine}
          {!a && (
            <button onClick={submitAnswer} disabled={!picked || busy} style={btnPrimary(!!picked && !busy)}>
              {busy ? '…' : t(lang, 'next')} <ChevronRight size={15} />
            </button>
          )}
          {a && a.correct && (
            <button onClick={nextQuestion} style={btnPrimary(true, '#16a34a')}>
              <CheckCircle size={16} /> {t(lang, 'correct')} — {t(lang, 'next')}
            </button>
          )}
          {a && !a.correct && (
            <button onClick={retry} style={btnPrimary(true)}>
              <RotateCcw size={15} /> {t(lang, 'tryAgain')}
            </button>
          )}
          {previewNote}
        </div>
      </div>
    );
  }

  // ── sign ──────────────────────────────────────────────────────────────────
  if (step === 'sign') {
    const ready = allAcked && allAnswered && !!signature && finalTick;
    return (
      <div style={wrapStyle}>
        {header}
        <div style={{ padding: '16px 18px 0' }}>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--hp-text)' }}>{t(lang, 'signTitle')}</div>

          <div style={{ fontSize: '0.6rem', fontWeight: 900, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--hp-dim)', margin: '16px 0 8px' }}>
            {t(lang, 'reviewTitle')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {clauses.map(c => (
              /* flex:1 + minWidth:0 so the line shrinks rather than wrapping under
                 its own tick: the admin shell sets flex-wrap:wrap below 768px. */
              <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                <CheckCircle size={14} color="#16a34a" style={{ flexShrink: 0, marginTop: 3 }} />
                <div style={{ flex: 1, minWidth: 0, fontSize: '0.82rem', color: 'var(--hp-text)', lineHeight: 1.45 }}>
                  <span style={{ color: 'var(--hp-dim)', fontWeight: 700 }}>{c.n}. </span>{c.heading}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 16, padding: '11px 13px', background: 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 8 }}>
            {(decl.closing || []).map((line, i) => (
              <div key={i} style={{ fontSize: '0.8rem', color: 'var(--hp-text)', lineHeight: 1.55, marginBottom: 8 }}>{line}</div>
            ))}
            <div style={{ fontSize: '0.88rem', fontWeight: 800, color: 'var(--hp-text)', lineHeight: 1.5 }}>{decl.acknowledgement}</div>
            {decl.acknowledgementTranslated && (
              <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', lineHeight: 1.55, marginTop: 8 }}>{decl.acknowledgementTranslated}</div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 14 }}>
            <div>
              <div style={{ fontSize: '0.58rem', fontWeight: 900, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>{t(lang, 'yourName')}</div>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--hp-text)', marginTop: 3 }}>{who.name || who.login}</div>
            </div>
            {who.contractNumber && (
              <div>
                <div style={{ fontSize: '0.58rem', fontWeight: 900, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>{t(lang, 'contract')}</div>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--hp-text)', marginTop: 3 }}>{who.contractNumber}</div>
              </div>
            )}
          </div>

          <div style={{ marginTop: 14 }}>
            <SignaturePad onChange={setSignature} defaultName={who.name || ''} />
          </div>

          <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={finalTick} onChange={e => setFinalTick(e.target.checked)}
              style={{ marginTop: 3, width: 17, height: 17, accentColor: '#b91c1c', flexShrink: 0 }} />
            <span style={{ fontSize: '0.82rem', color: 'var(--hp-text)', lineHeight: 1.5 }}>{t(lang, 'ackBox')}</span>
          </label>
        </div>

        <div style={{ padding: '14px 18px 16px' }}>
          {errorLine}
          <button onClick={submitSignature} disabled={!ready || busy} style={btnPrimary(ready && !busy)}>
            {busy ? <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <FileSignature size={16} />}
            {busy ? t(lang, 'signing') : t(lang, 'signBtn')}
          </button>
          {previewNote}
        </div>
      </div>
    );
  }

  // ── done ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ ...wrapStyle, borderColor: '#16a34a' }}>
      <div style={{ background: '#16a34a', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <CheckCircle size={15} color="#fff" />
        <span style={{ fontSize: '0.66rem', fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#fff' }}>
          {t(lang, 'doneTitle')}
        </span>
      </div>
      <div style={{ padding: '18px' }}>
        <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--hp-text)' }}>{decl.title}</div>
        <div style={{ fontSize: '0.83rem', color: 'var(--hp-dim)', marginTop: 8, lineHeight: 1.55 }}>
          {preview ? 'That is the whole declaration. Nothing was recorded.' : t(lang, 'doneBody')}
        </div>
        {done && done.verifyCode && (
          <div style={{ marginTop: 12, fontFamily: 'var(--font-mono)', fontSize: '0.85rem', color: 'var(--hp-gold)', fontWeight: 700 }}>
            {done.verifyCode}
          </div>
        )}
        {!preview && onCleared && (
          <button onClick={onCleared} style={{ ...btnPrimary(true, '#16a34a'), marginTop: 16 }}>
            <ChevronRight size={16} /> {t(lang, 'continueBtn')}
          </button>
        )}
      </div>
    </div>
  );
}

// A small read-only card for the portal: what you signed, when, and the code. Renders
// nothing at all when there is no declaration and nothing outstanding, so it never
// takes up room on a home screen for no reason.
export function SecurityDeclarationCard({ onOpen }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    let live = true;
    post({ action: 'view', lang: getDeclLang() }).then(d => { if (live && d && d.success) setState(d); }).catch(() => {});
    return () => { live = false; };
  }, []);
  if (!state || !state.compulsory) return null;
  const signed = state.signed;
  return (
    <div style={{
      background: 'var(--hp-surface)', border: `1px solid ${signed ? 'var(--hp-border)' : '#b91c1c'}`,
      borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 12,
      display: 'flex', alignItems: 'center', gap: 11, cursor: onOpen ? 'pointer' : 'default',
    }} onClick={onOpen}>
      {signed ? <ShieldAlert size={18} color="#16a34a" /> : <Lock size={18} color="#ef4444" />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--hp-text)' }}>
          {state.declaration.title}
        </div>
        <div style={{ fontSize: '0.73rem', color: 'var(--hp-dim)', marginTop: 2 }}>
          {signed
            ? `Signed ${new Date(signed.signedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })} · ${signed.verifyCode}`
            : 'Not signed yet — required before your onboard scan'}
        </div>
      </div>
      {onOpen && <ChevronRight size={16} color="var(--hp-dim)" />}
    </div>
  );
}

export default SecurityDeclarationGate;
